/**
 * SimpleSlider Module - Following guidelines
 * Pure Tailwind, no inline styles, array mapping
 */

import { debugLog } from '../debug/logger.js'

class SimpleSlider {
	constructor() {
		// Configuration using arrays
		this.CONFIG = {
			SELECTORS: {
				slider: '.ui-s',
				slides: '.ui-s-slides',
				slide: '.ui-s-slide',
				prevBtn: '.ui-s-prev',
				nextBtn: '.ui-s-next',
				dots: '.ui-s-dots',
				dot: '.ui-s-dot',
			},
			STATES: {
				dot: {
					active: ['bg-current', 'opacity-100'],
					inactive: ['bg-current', 'opacity-50'],
				},
			},
			TIMING: {
				TRANSITION: 300,
				AUTO_PLAY: 5000,
			},
		}

		// Initialize all sliders
		this.initAll()
	}

	initAll() {
		const sliders = document.querySelectorAll(this.CONFIG.SELECTORS.slider)

		sliders.forEach(slider => {
			this.initSlider(slider)
		})

		if (sliders.length > 0) {
			debugLog.success('SimpleSlider: Initialized', sliders.length, 'sliders')
		}
	}

	initSlider(sliderElement) {
		const sliderId = sliderElement.dataset.sliderId
		if (!sliderId) {
			debugLog.warn('SimpleSlider: No slider ID found', sliderElement)
			return
		}

		// Get slider elements
		const elements = {
			slider: sliderElement,
			slides: sliderElement.querySelector(this.CONFIG.SELECTORS.slides),
			slideItems: sliderElement.querySelectorAll(this.CONFIG.SELECTORS.slide),
			prevBtn: sliderElement.querySelector(this.CONFIG.SELECTORS.prevBtn),
			nextBtn: sliderElement.querySelector(this.CONFIG.SELECTORS.nextBtn),
			dots: sliderElement.querySelector(this.CONFIG.SELECTORS.dots),
		}

		// Skip if no slides
		if (!elements.slides || elements.slideItems.length === 0) {
			debugLog.warn('SimpleSlider: No slides found for', sliderId)
			return
		}

		// Store state
		const state = {
			currentIndex: 0,
			totalSlides: elements.slideItems.length,
			autoPlayTimer: null,
		}

		// Store state on element for access
		sliderElement.sliderState = state

		// Bind events
		this.bindSliderEvents(elements, state)

		// Initialize dots if present
		if (elements.dots) {
			this.createDots(elements, state)
		}

		// Set initial navigation color based on first slide
		this.updateNavigationColor(elements, state)

		// Start autoplay if configured
		if (sliderElement.dataset.autoplay === 'true') {
			this.startAutoPlay(elements, state)
		}

		debugLog.info('SimpleSlider: Initialized slider', sliderId)
	}

	bindSliderEvents(elements, state) {
		// Previous button
		if (elements.prevBtn) {
			elements.prevBtn.addEventListener('click', e => {
				e.preventDefault()
				this.moveSlide(elements, state, -1)
			})
		}

		// Next button
		if (elements.nextBtn) {
			elements.nextBtn.addEventListener('click', e => {
				e.preventDefault()
				this.moveSlide(elements, state, 1)
			})
		}

		// Pause autoplay on hover
		if (state.autoPlayTimer) {
			elements.slider.addEventListener('mouseenter', () => {
				this.stopAutoPlay(state)
			})

			elements.slider.addEventListener('mouseleave', () => {
				this.startAutoPlay(elements, state)
			})
		}

		// Touch support
		this.addTouchSupport(elements, state)
	}

	moveSlide(elements, state, direction) {
		// Calculate new index
		state.currentIndex += direction

		// Wrap around
		if (state.currentIndex < 0) {
			state.currentIndex = state.totalSlides - 1
		} else if (state.currentIndex >= state.totalSlides) {
			state.currentIndex = 0
		}

		// Apply transform
		const offset = -state.currentIndex * 100
		elements.slides.style.transform = `translateX(${offset}%)`

		// Update dots AND navigation color
		this.updateDots(elements, state)
		this.updateNavigationColor(elements, state)

		debugLog.log('SimpleSlider: Moved to slide', state.currentIndex)
	}

	setSlide(elements, state, index) {
		// Set specific slide
		state.currentIndex = index

		// Apply transform
		const offset = -state.currentIndex * 100
		elements.slides.style.transform = `translateX(${offset}%)`

		// Update dots AND navigation color
		this.updateDots(elements, state)
		this.updateNavigationColor(elements, state)
	}

	createDots(elements, state) {
		// Clear existing dots
		elements.dots.innerHTML = ''

		// Create dot for each slide
		for (let i = 0; i < state.totalSlides; i++) {
			const dot = document.createElement('button')
			dot.className =
				'ui-s-dot w-2 h-2 rounded-full transition-all duration-300 ' +
				(i === 0 ? this.CONFIG.STATES.dot.active.join(' ') : this.CONFIG.STATES.dot.inactive.join(' '))
			dot.setAttribute('aria-label', `Go to slide ${i + 1}`)

			dot.addEventListener('click', e => {
				e.preventDefault()
				this.setSlide(elements, state, i)
			})

			elements.dots.appendChild(dot)
		}
	}

	updateDots(elements, state) {
		if (!elements.dots) return

		const dots = elements.dots.querySelectorAll(this.CONFIG.SELECTORS.dot)
		const { STATES } = this.CONFIG

		dots.forEach((dot, index) => {
			if (index === state.currentIndex) {
				this.swapClasses(dot, STATES.dot.inactive, STATES.dot.active)
			} else {
				this.swapClasses(dot, STATES.dot.active, STATES.dot.inactive)
			}
		})
	}

	updateNavigationColor(elements, state) {
		// Get the current slide element
		const currentSlide = elements.slideItems[state.currentIndex]
		if (!currentSlide) return

		// Get computed text color from the current slide
		const slideColor = window.getComputedStyle(currentSlide).color

		// Find navigation container (parent of dots)
		const navContainer = elements.dots?.parentElement
		if (navContainer) {
			// Update the text color of the navigation container
			// This will cascade to all children using currentColor
			navContainer.style.color = slideColor
		}

		debugLog.log('SimpleSlider: Updated navigation color to', slideColor)
	}

	startAutoPlay(elements, state) {
		if (state.autoPlayTimer) return

		state.autoPlayTimer = setInterval(() => {
			this.moveSlide(elements, state, 1)
		}, this.CONFIG.TIMING.AUTO_PLAY)
	}

	stopAutoPlay(state) {
		if (state.autoPlayTimer) {
			clearInterval(state.autoPlayTimer)
			state.autoPlayTimer = null
		}
	}

	addTouchSupport(elements, state) {
		let touchStartX = 0
		let touchEndX = 0

		elements.slider.addEventListener('touchstart', e => {
			touchStartX = e.changedTouches[0].screenX
		})

		elements.slider.addEventListener('touchend', e => {
			touchEndX = e.changedTouches[0].screenX
			this.handleSwipe(elements, state, touchStartX, touchEndX)
		})
	}

	handleSwipe(elements, state, startX, endX) {
		const diff = startX - endX
		const threshold = 50

		if (Math.abs(diff) > threshold) {
			if (diff > 0) {
				// Swipe left - next slide
				this.moveSlide(elements, state, 1)
			} else {
				// Swipe right - previous slide
				this.moveSlide(elements, state, -1)
			}
		}
	}

	/**
	 * Helper to swap class arrays cleanly
	 */
	swapClasses(element, remove, add) {
		if (!element) return
		element.classList.remove(...remove)
		element.classList.add(...add)
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new SimpleSlider())
} else {
	new SimpleSlider()
}

// Export for use
window.SimpleSlider = SimpleSlider
